import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

class IdmlService {
    /**
     * Upload a new IDML book from a single zip package containing the .idml file plus, optionally,
     * its "Document Fonts" and "Links" folders (the layout InDesign's own Package export produces).
     */
    async createBook(path, name, zipFile, sourceLanguage, targetLanguages = [], autoTranslate = false, translationPrompt = '') {
        const headers = getAuthHeaders(true);
        const formData = new FormData();
        formData.append('zip', zipFile);
        formData.append('path', path);
        formData.append('name', name);
        formData.append('sourceLanguage', sourceLanguage);
        targetLanguages.forEach((lang) => formData.append('targetLanguages', lang));
        formData.append('autoTranslate', String(autoTranslate));
        // Milestone 7 (stored only for now, see design doc): book-level translation guidance.
        if (translationPrompt) {
            formData.append('translationPrompt', translationPrompt);
        }

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v2/idml/books`, formData, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    }

    /**
     * Attach a "Document Fonts"/"Links" folder to a book that's already been uploaded and
     * parsed, without re-uploading the .idml itself -- e.g. the folder was forgotten at
     * creation time, or the book's Links didn't resolve the first time.
     */
    async reparseAssets(bookId, fonts = [], images = []) {
        const headers = getAuthHeaders(true);
        const formData = new FormData();
        fonts.forEach((fontFile) => formData.append('fonts', fontFile));
        images.forEach((imageFile) => formData.append('images', imageFile));

        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/reparse-assets`,
            formData,
            { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    }

    /**
     * TEMPORARY, testing-only: repairs an already-parsed book whose table content (e.g. a
     * contents/TOC page) was never linked to its frame, without a full re-upload. Remove this
     * once existing test books have been repaired or re-uploaded -- new uploads don't need it.
     */
    async repairTableLinks(bookId) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/repair-table-links`,
            {},
            { headers },
        );
        return response.data;
    }

    async listBooks() {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/books`, { headers });
        return response.data;
    }

    /**
     * Fetch a book. Set autoTranslate=true to let the server chain PARSED -> translate for
     * any target languages that were selected at creation time but haven't started yet.
     */
    async getBook(bookId, autoTranslate = false) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}`, {
            headers,
            params: { autoTranslate: String(autoTranslate) },
        });
        return response.data; // { book, effectivePermission }
    }

    async addLanguage(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages`,
            { languageCode },
            { headers },
        );
        return response.data;
    }

    async getSegments(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/segments`, {
            headers,
            params: { languageCode },
        });
        return response.data;
    }

    async patchSegment(bookId, segmentId, { languageCode, text, runStructure, needsReview, saveType }) {
        const headers = getAuthHeaders(true);
        const response = await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/segments/${segmentId}`,
            { languageCode, text, runStructure, needsReview, saveType },
            { headers },
        );
        return response.data;
    }

    async getPageLayout(bookId, pageIndex, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/pages/${pageIndex}/layout`,
            { headers, params: { languageCode } },
        );
        return response.data;
    }

    // --- Copy-fitting (design doc §4/§6) ---

    async refitSegment(bookId, segmentId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/segments/${segmentId}/refit`,
            { languageCode },
            { headers },
        );
        return response.data;
    }

    async setCopyFitOverride(bookId, segmentId, languageCode, copyFitAdjustment) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/segments/${segmentId}/copy-fit-override`,
            { languageCode, copyFitAdjustment },
            { headers },
        );
        return response.data;
    }

    async refitBook(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/refit`,
            { languageCode },
            { headers },
        );
        return response.data;
    }

    async getReviewStatus(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/review-status`, {
            headers,
            params: { languageCode },
        });
        return response.data;
    }

    async getReviewReport(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/review-report`, {
            headers,
            params: { languageCode },
        });
        return response.data;
    }

    /** Bulk "approve all" for one book/language: clears needsReview on every currently-flagged segment. */
    async approveAllReviews(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/approve-all-reviews`,
            null,
            { headers },
        );
        return response.data; // { approvedCount }
    }

    async getHistory(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/history/${languageCode}`,
            { headers },
        );
        return response.data;
    }

    async restoreVersion(bookId, languageCode, versionNumber) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/history/${languageCode}/restore`,
            { versionNumber },
            { headers },
        );
        return response.data;
    }

    // --- Fonts (design doc §Milestone 5) ---

    async getFontCatalog() {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/idml/fonts/catalog`, { headers });
        return response.data;
    }

    /**
     * Fetches a curated catalog font's raw bytes and returns a blob object URL suitable for a CSS
     * `@font-face` `src`. The endpoint is auth-gated like the rest of this API, so a plain CSS
     * `url("/api/.../file")` can't be used directly (the browser won't attach our auth/email
     * headers to a font fetch) -- this is the fetch-then-blob-URL pattern IdmlPageSpreadEditor uses
     * to actually load real fonts into the page-spread preview instead of only ever showing the
     * generic fallback stack.
     */
    async getFontCatalogFileBlobUrl(family) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/fonts/catalog/${encodeURIComponent(family)}/file`,
            { headers, responseType: 'blob' },
        );
        return URL.createObjectURL(response.data);
    }

    async setFontOverride(bookId, languageCode, family) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/font-override`,
            { family },
            { headers },
        );
        return response.data;
    }

    // --- Translation prompt (design doc §Milestone 7 -- stored only; not yet applied to T2TT) ---

    async setTranslationPrompt(bookId, prompt) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/translation-prompt`,
            { prompt },
            { headers },
        );
        return response.data;
    }

    // --- Export (design doc §5/§6) ---

    async exportBook(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/export`,
            null,
            { headers },
        );
        return response.data;
    }

    async getExportStatus(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/export/status`,
            { headers },
        );
        return response.data;
    }

    // --- Edit session (mirrors TextFileService's session endpoints) ---

    async startEditSession(bookId, languageCode, sessionId) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/edit-session/start`,
            null,
            { headers, params: sessionId ? { sessionId } : {} },
        );
        return response.data;
    }

    async keepAliveEditSession(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/edit-session/keep-alive`,
            null,
            { headers },
        );
        return response.data;
    }

    async endEditSession(bookId, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/idml/books/${bookId}/languages/${languageCode}/edit-session/end`,
            null,
            { headers },
        );
        return response.data;
    }
}

const idmlServiceInstance = new IdmlService();
export default idmlServiceInstance;
